import React, { useMemo, useCallback } from 'react';
import ReactFlow, {
  ReactFlowProvider,
  Background,
  Controls,
  Handle,
  Position,
  useNodesState,
  useEdgesState,
  addEdge,
} from 'reactflow';
import { Package, Truck, CheckCircle, Clock, AlertTriangle, PlayCircle } from 'lucide-react';

// Custom node component for status progression
const StatusNode = ({ data }) => {
  const {
    label,
    status,
    isActive,
    isCompleted,
    isPending,
    fulfillmentPercentage,
    icon: IconComponent,
    description,
    timestamp
  } = data;

  const getNodeStyles = () => {
    if (isCompleted) {
      return {
        border: '2px solid #10b981',
        background: 'linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)',
        color: '#065f46'
      };
    } else if (isActive) {
      return {
        border: '2px solid #fed402',
        background: 'linear-gradient(135deg, #fef3c7 0%, #fed402 100%)',
        color: '#92400e'
      };
    } else if (isPending) {
      return {
        border: '2px solid #d1d5db',
        background: 'linear-gradient(135deg, #f9fafb 0%, #e5e7eb 100%)',
        color: '#6b7280'
      };
    } else {
      return {
        border: '2px solid #f59e0b',
        background: 'linear-gradient(135deg, #fef3c7 0%, #fbbf24 100%)',
        color: '#92400e'
      };
    }
  };

  return (
    <div
      style={getNodeStyles()}
      className="px-6 py-4 rounded-lg shadow-lg min-w-[200px] transition-all duration-300 hover:shadow-xl"
    >
      <Handle type="target" position={Position.Left} className="opacity-0" />
      
      <div className="flex flex-col items-center text-center">
        <div className="flex items-center justify-center w-12 h-12 rounded-full bg-white/30 mb-3">
          <IconComponent className="h-6 w-6" />
        </div>
        
        <h3 className="font-semibold text-sm mb-1">{label}</h3>
        
        {description && (
          <p className="text-xs opacity-80 mb-2">{description}</p>
        )}
        
        {fulfillmentPercentage !== null && fulfillmentPercentage !== undefined && (
          <div className="w-full">
            <div className="flex justify-between text-xs mb-1">
              <span>Progress</span>
              <span>{fulfillmentPercentage}%</span>
            </div>
            <div className="w-full bg-white/30 rounded-full h-2">
              <div
                className="bg-current h-2 rounded-full transition-all duration-500"
                style={{ width: `${fulfillmentPercentage}%` }}
              />
            </div>
          </div>
        )}
        
        {timestamp && (
          <div className="text-xs opacity-70 mt-2">
            {new Date(timestamp).toLocaleDateString()}
          </div>
        )}
      </div>
      
      <Handle type="source" position={Position.Right} className="opacity-0" />
    </div>
  );
};

// Define the status progression stages
const STATUS_STAGES = [
  {
    id: 'created',
    label: 'Order Created',
    description: 'Order received and recorded',
    icon: Package,
    statuses: ['Pending', 'Created', 'New']
  },
  {
    id: 'processing',
    label: 'Processing',
    description: 'Order being processed',
    icon: PlayCircle,
    statuses: ['Processing', 'Open', 'In Progress']
  },
  {
    id: 'picking',
    label: 'Picking',
    description: 'Items being picked',
    icon: Package,
    statuses: ['Being Picked', 'Picking in Progress']
  },
  {
    id: 'committed',
    label: 'Committed',
    description: 'Ready for shipment',
    icon: CheckCircle,
    statuses: ['Committed', 'Committed for Shipment']
  },
  {
    id: 'shipping',
    label: 'Shipping',
    description: 'In transit to customer',
    icon: Truck,
    statuses: ['Partially Shipped', 'Shipping', 'Dispatched']
  },
  {
    id: 'completed',
    label: 'Completed',
    description: 'Order fulfilled',
    icon: CheckCircle,
    statuses: ['Fully Shipped', 'Complete', 'Closed', 'Finalized']
  }
];

const StatusProgressionFlow = ({ order, fulfillmentMetrics, onRefresh }) => {
  // Create nodes and edges for the flow
  const { nodes, edges } = useMemo(() => {
    if (!order) {
      return { nodes: [], edges: [] };
    }

    const currentStatus = order.status || 'Pending';
    const currentFulfillment = fulfillmentMetrics?.fulfillment_percentage || 0;
    
    // Determine current stage based on status and fulfillment
    const getCurrentStage = () => {
      // Check for completed first
      if (currentFulfillment >= 100 || STATUS_STAGES[5].statuses.some(s => 
        currentStatus.toLowerCase().includes(s.toLowerCase())
      )) {
        return 5; // Completed
      }
      
      // Check other stages
      for (let i = 0; i < STATUS_STAGES.length - 1; i++) {
        if (STATUS_STAGES[i].statuses.some(s => 
          currentStatus.toLowerCase().includes(s.toLowerCase())
        )) {
          return i;
        }
      }
      
      // Default to processing if status doesn't match
      return 1;
    };

    const currentStageIndex = getCurrentStage();

    // Create nodes
    const flowNodes = STATUS_STAGES.map((stage, index) => {
      const isCompleted = index < currentStageIndex;
      const isActive = index === currentStageIndex;
      const isPending = index > currentStageIndex;
      
      // Calculate fulfillment percentage for this stage
      let stageProgress = 0;
      if (isCompleted) {
        stageProgress = 100;
      } else if (isActive) {
        stageProgress = currentFulfillment;
      }

      return {
        id: stage.id,
        type: 'custom',
        position: { x: index * 250, y: 50 },
        data: {
          label: stage.label,
          status: currentStatus,
          isActive,
          isCompleted,
          isPending,
          fulfillmentPercentage: stageProgress,
          icon: stage.icon,
          description: stage.description,
          timestamp: isCompleted || isActive ? order.last_modified || order.created_at : null
        }
      };
    });

    // Create edges between nodes
    const flowEdges = [];
    for (let i = 0; i < STATUS_STAGES.length - 1; i++) {
      const isCompleted = i < currentStageIndex;
      const isActive = i === currentStageIndex - 1; // Edge leading to current stage
      
      flowEdges.push({
        id: `edge-${i}`,
        source: STATUS_STAGES[i].id,
        target: STATUS_STAGES[i + 1].id,
        type: 'smoothstep',
        animated: isActive,
        style: {
          stroke: isCompleted || isActive ? '#10b981' : '#d1d5db',
          strokeWidth: isCompleted || isActive ? 3 : 2,
        }
      });
    }

    return { nodes: flowNodes, edges: flowEdges };
  }, [order, fulfillmentMetrics]);

  const [flowNodes, setNodes, onNodesChange] = useNodesState(nodes);
  const [flowEdges, setEdges, onEdgesChange] = useEdgesState(edges);

  // Custom node types
  const nodeTypes = useMemo(
    () => ({
      custom: StatusNode,
    }),
    []
  );

  const onConnect = useCallback(
    (params) => setEdges((eds) => addEdge(params, eds)),
    [setEdges]
  );

  return (
    <div className="h-full w-full">
      <ReactFlowProvider>
        <ReactFlow
          nodes={flowNodes}
          edges={flowEdges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          nodeTypes={nodeTypes}
          fitView
          fitViewOptions={{
            padding: 0.3,
            includeHiddenNodes: false
          }}
          minZoom={0.5}
          maxZoom={1.5}
          defaultViewport={{ x: 0, y: 0, zoom: 1 }}
          proOptions={{ hideAttribution: true }}
        >
          <Background color="#f1f5f9" gap={20} />
          <Controls showInteractive={false} />
          
          {/* Custom Panel with Order Info */}
          <div className="absolute top-4 left-4 bg-white rounded-lg shadow-lg p-4 z-10">
            <div className="flex items-center space-x-3">
              <div className="flex items-center justify-center w-10 h-10 rounded-full bg-brand text-brand-dark">
                <Package className="h-5 w-5" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">{order?.order_number}</h3>
                <p className="text-sm text-gray-600">
                  {fulfillmentMetrics?.fulfillment_summary || order?.status || 'Processing'}
                </p>
              </div>
            </div>
            
            {fulfillmentMetrics && (
              <div className="mt-3 pt-3 border-t border-gray-200">
                <div className="flex justify-between text-sm text-gray-600 mb-1">
                  <span>Overall Progress</span>
                  <span className="font-medium">{fulfillmentMetrics.fulfillment_percentage}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-brand h-2 rounded-full transition-all duration-500"
                    style={{ width: `${fulfillmentMetrics.fulfillment_percentage}%` }}
                  />
                </div>
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>{fulfillmentMetrics.fully_shipped_lines || 0} shipped</span>
                  <span>{fulfillmentMetrics.total_lines || 0} total items</span>
                </div>
              </div>
            )}
          </div>
        </ReactFlow>
      </ReactFlowProvider>
    </div>
  );
};

export default StatusProgressionFlow;
